/**
 * The signed-in workspace account: the current browser session's platform
 * user profile (name, email, avatar).
 *
 * Same shape as `state/orgs.ts`: one cached atom over the server's
 * `/api/amoreg/auth/me` endpoint, read synchronously
 * through `useAccountProfile`. The account menu and the Account settings
 * page both render from it, and avatar updates refresh it so every surface
 * agrees immediately.
 *
 * Browser cookie sessions only: the desktop app manages its own trust and
 * has no workspace account to show.
 *
 * @module state/accountProfile
 */
import { useAtomValue } from "@effect/atom-react";
import * as Effect from "effect/Effect";
import * as Layer from "effect/Layer";
import * as Option from "effect/Option";
import * as Schema from "effect/Schema";
import { AsyncResult, Atom } from "effect/unstable/reactivity";
import { useCallback, useMemo } from "react";

import { resolvePrimaryEnvironmentHttpUrl } from "../environments/primary/target";
import { appAtomRegistry } from "../rpc/atomRegistry";

const ProfileSchema = Schema.Struct({
  subject: Schema.String,
  email: Schema.optional(Schema.String),
  firstName: Schema.optional(Schema.NullOr(Schema.String)),
  lastName: Schema.optional(Schema.NullOr(Schema.String)),
  profilePicture: Schema.optional(Schema.NullOr(Schema.String)),
  /** The web app's own per-person state (`User.metadata`), e.g. the tour. */
  metadata: Schema.optional(Schema.NullOr(Schema.Record(Schema.String, Schema.Unknown))),
});

export interface WorkspaceAccount {
  readonly subject: string;
  readonly email?: string | undefined;
  readonly firstName?: string | null | undefined;
  readonly lastName?: string | null | undefined;
  readonly profilePicture?: string | null | undefined;
  readonly metadata?: Readonly<Record<string, unknown>> | null | undefined;
}

/** The person's own context for eve, as the Settings page edits it. */
export const UserContextSchema = Schema.Struct({
  personalContext: Schema.NullOr(Schema.String),
  jobTitle: Schema.NullOr(Schema.String),
  department: Schema.NullOr(Schema.String),
  workingStyle: Schema.NullOr(Schema.String),
  responsePreferences: Schema.NullOr(Schema.String),
  pinnedInstructions: Schema.Array(Schema.String),
  automaticRecall: Schema.Boolean.pipe(Schema.withDecodingDefault(Effect.succeed(true))),
  learnFromConversations: Schema.Boolean.pipe(Schema.withDecodingDefault(Effect.succeed(true))),
});
export type UserContext = typeof UserContextSchema.Type;

export const EMPTY_USER_CONTEXT: UserContext = {
  personalContext: null,
  jobTitle: null,
  department: null,
  workingStyle: null,
  responsePreferences: null,
  pinnedInstructions: [],
  automaticRecall: true,
  learnFromConversations: true,
};

const decodeProfile = Schema.decodeUnknownEffect(ProfileSchema);

/** "workos:amir@amoreg.com" → "amir@amoreg.com"; other subjects pass through. */
export function displaySubject(subject: string): string {
  return subject.startsWith("workos:") ? subject.slice("workos:".length) : subject;
}

export function displayName(account: WorkspaceAccount): string | null {
  const name = [account.firstName, account.lastName].filter(Boolean).join(" ").trim();
  return name.length > 0 ? name : null;
}

export function accountInitials(account: WorkspaceAccount): string {
  const name = displayName(account);
  if (name) {
    const parts = name.split(/\s+/);
    return ((parts[0]?.[0] ?? "") + (parts[1]?.[0] ?? "")).toUpperCase() || "??";
  }
  const letters = displaySubject(account.subject).replace(/[^a-zA-Z]/g, "");
  return (letters.slice(0, 2) || "??").toUpperCase();
}

export const fetchAccount = Effect.gen(function* () {
  if (typeof window === "undefined" || window.desktopBridge !== undefined) {
    return null;
  }
  // `/me` authenticates the current cookie itself. Listing all environment
  // clients requires access:read, which ordinary workspace members lack.
  const url = resolvePrimaryEnvironmentHttpUrl("/api/amoreg/auth/me");
  const response = yield* Effect.tryPromise(() => fetch(url, { credentials: "include" }));
  if (!response.ok) return null;
  const json = yield* Effect.tryPromise(() => response.json());
  return yield* decodeProfile(json);
}).pipe(Effect.orElseSucceed(() => null));

const accountRuntime = Atom.runtime(Layer.empty);
const accountAtom = accountRuntime
  .atom(fetchAccount)
  .pipe(Atom.withLabel("web-amoreg:account-profile"));

export interface AccountProfileView {
  /** Null while loading, on desktop, and for sessions without an account. */
  readonly account: WorkspaceAccount | null;
  /** True until the first response arrives. */
  readonly isPending: boolean;
  readonly refresh: () => void;
}

/** Re-read the profile for the current session (see refreshOrgs). */
export function refreshAccountProfile(): void {
  appAtomRegistry.refresh(accountAtom);
}

export function useAccountProfile(): AccountProfileView {
  const result = useAtomValue(accountAtom);

  const refresh = useCallback(() => {
    appAtomRegistry.refresh(accountAtom);
  }, []);

  return useMemo(() => {
    const account = Option.getOrNull(AsyncResult.value(result)) ?? null;
    return {
      account,
      isPending: result.waiting && account === null,
      refresh,
    };
  }, [result, refresh]);
}

/**
 * Ask the server to email a six-digit verification code to `newEmail` (the
 * first step of changing the sign-in address). Resolves to an error message
 * for the dialog to show, or null when the code is on its way.
 */
export async function startEmailChange(newEmail: string): Promise<string | null> {
  const url = resolvePrimaryEnvironmentHttpUrl("/api/amoreg/auth/email-change/start");
  try {
    const response = await fetch(url, {
      method: "POST",
      credentials: "include",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ newEmail }),
    });
    if (!response.ok) {
      return (await response.text().catch(() => "")) || "The verification code could not be sent.";
    }
    return null;
  } catch {
    return "The verification code could not be sent.";
  }
}

/**
 * Confirm the emailed code and perform the address swap. On success the
 * server revokes this browser session (its subject names the old address),
 * so the caller must bounce through `/pair` to re-mint one.
 */
export async function confirmEmailChange(
  code: string,
): Promise<{ ok: true; newEmail: string } | { ok: false; message: string }> {
  const url = resolvePrimaryEnvironmentHttpUrl("/api/amoreg/auth/email-change/confirm");
  try {
    const response = await fetch(url, {
      method: "POST",
      credentials: "include",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ code }),
    });
    if (!response.ok) {
      return {
        ok: false,
        message: (await response.text().catch(() => "")) || "The email could not be changed.",
      };
    }
    const body = (await response.json().catch(() => null)) as { newEmail?: string } | null;
    return { ok: true, newEmail: body?.newEmail ?? "" };
  } catch {
    return { ok: false, message: "The email could not be changed." };
  }
}

/**
 * Persist a new avatar (a small image data URL) or clear it (null) on the
 * platform user, then refresh the shared account snapshot. Resolves to an
 * error message for the settings row to show, or null on success.
 */
export async function updateProfilePicture(profilePicture: string | null): Promise<string | null> {
  const url = resolvePrimaryEnvironmentHttpUrl("/api/amoreg/auth/profile-picture");
  try {
    const response = await fetch(url, {
      method: "POST",
      credentials: "include",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ profilePicture }),
    });
    if (!response.ok) {
      return response.status === 404
        ? "This session has no platform profile to update."
        : (await response.text().catch(() => "")) || "The picture could not be saved.";
    }
    appAtomRegistry.refresh(accountAtom);
    return null;
  } catch {
    return "The picture could not be saved.";
  }
}

/**
 * Save the person's own first and last name on the platform user (the server
 * trims them and stores a blank last name as null), then refresh the shared
 * account snapshot so the menu and roster agree. Resolves to an error message
 * for the settings row to show, or null on success.
 */
export async function updateProfileName(name: {
  readonly firstName: string;
  readonly lastName: string;
}): Promise<string | null> {
  const url = resolvePrimaryEnvironmentHttpUrl("/api/amoreg/auth/name");
  try {
    const response = await fetch(url, {
      method: "POST",
      credentials: "include",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ firstName: name.firstName, lastName: name.lastName }),
    });
    if (!response.ok) {
      return response.status === 404
        ? "This session has no platform profile to update."
        : (await response.text().catch(() => "")) || "Your name could not be saved.";
    }
    appAtomRegistry.refresh(accountAtom);
    return null;
  } catch {
    return "Your name could not be saved.";
  }
}

/**
 * Merge a patch into the person's `User.metadata` (the server allow-lists
 * the keys), then refresh the account snapshot so readers see it.
 */
export async function updateAccountMetadata(
  patch: Record<string, unknown>,
): Promise<string | null> {
  const url = resolvePrimaryEnvironmentHttpUrl("/api/amoreg/auth/metadata");
  try {
    const response = await fetch(url, {
      method: "POST",
      credentials: "include",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(patch),
    });
    if (!response.ok) {
      return (await response.text().catch(() => "")) || "The setting could not be saved.";
    }
    appAtomRegistry.refresh(accountAtom);
    return null;
  } catch {
    return "The setting could not be saved.";
  }
}

const decodeUserContext = Schema.decodeUnknownEffect(UserContextSchema);

/** Null on failure: never turn a failed preference read into default-on settings. */
export async function fetchUserContext(): Promise<UserContext | null> {
  const url = resolvePrimaryEnvironmentHttpUrl("/api/amoreg/auth/context");
  try {
    const response = await fetch(url, { credentials: "include" });
    if (!response.ok) return null;
    return await Effect.runPromise(decodeUserContext(await response.json()));
  } catch {
    return null;
  }
}

/** Save the whole context. Resolves to an error message, or null on success. */
export async function saveUserContext(context: UserContext): Promise<string | null> {
  const url = resolvePrimaryEnvironmentHttpUrl("/api/amoreg/auth/context");
  try {
    const response = await fetch(url, {
      method: "POST",
      credentials: "include",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(context),
    });
    if (!response.ok) {
      return response.status === 404
        ? "This session has no platform profile to update."
        : (await response.text().catch(() => "")) || "Your context could not be saved.";
    }
    return null;
  } catch {
    return "Your context could not be saved.";
  }
}
